import { useMemo } from "react";
import type { FilterValue } from "@/components/filter-popover";
import {
  extractArrayFromFilter,
  extractTextFromFilter,
} from "@/lib/filter-value-utils";
import type { Activity } from "./api/use-account-activities";

function matchesFilters(
  activity: Activity,
  filters: Record<string, FilterValue>,
): boolean {
  if (Object.keys(filters).length === 0) {
    return true;
  }

  // Extract filter values using utility functions
  const peopleFilter = extractArrayFromFilter(filters.people);
  const channelFilter = extractArrayFromFilter(filters.channel);
  const teamFilter = extractArrayFromFilter(filters.team);
  const activitySearchText = extractTextFromFilter(filters.activity);
  const statusFilter = extractArrayFromFilter(filters.status);
  const directionFilter = extractArrayFromFilter(filters.direction);

  const peopleMatch =
    !peopleFilter.length ||
    Boolean(
      activity.people?.some((person) => peopleFilter.includes(person.id)),
    );

  const channelMatch =
    !channelFilter.length || channelFilter.includes(activity.channel);

  const teamMatch =
    !teamFilter.length ||
    Boolean(
      activity.involved_team_ids?.some((teamId) => teamFilter.includes(teamId)),
    );

  const statusMatch =
    !statusFilter.length || statusFilter.includes(activity.status);

  const directionMatch =
    !directionFilter.length ||
    (activity.direction !== undefined &&
      directionFilter.includes(activity.direction));

  const searchMatch =
    !activitySearchText ||
    (activity.activity?.toLowerCase() || "").includes(
      activitySearchText.toLowerCase(),
    ) ||
    (activity.channel?.toLowerCase() || "").includes(
      activitySearchText.toLowerCase(),
    );

  return (
    peopleMatch &&
    channelMatch &&
    teamMatch &&
    statusMatch &&
    directionMatch &&
    searchMatch
  );
}

export function useFilteredActivities(
  activities: Activity[] | undefined,
  filters: Record<string, FilterValue>,
) {
  return useMemo(() => {
    if (!activities) return [];

    return activities.filter((activity) => matchesFilters(activity, filters));
  }, [activities, filters]);
}
